import type { ClientChat } from '@DigitaleDoerfer/digitale-doerfer-api';
import { AnimatedLegendList } from '@legendapp/list/reanimated';
import { useScrollToTop } from '@react-navigation/native';
import { useMemo, useRef, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Button, Divider, Text } from 'react-native-paper';

import { useGetOwnChats } from '@/features/api/hooks/grapevine-chat/useGetOwnChats';
import { useMeinOrtScroll } from '@/features/mein-ort/components/MeinOrtScrollContext';
import { ChatListItem } from '@/features/profile-setting/components/ChatListItem';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { EmptyCard } from '@/shared/components/EmptyCard';
import { Refresher } from '@/shared/components/state-view/Refresher';

const makeStyles = () =>
  StyleSheet.create({
    list: { flex: 1 },
    content: {
      flexGrow: 1
    },
    center: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
      padding: 32
    },
    empty: { marginHorizontal: 16, marginTop: 8 }
  });

export const MeinOrtChatsTab: FC = () => {
  const styles = useMemo(() => makeStyles(), []);
  const { scrollY, feedInset } = useMeinOrtScroll();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.chats'
  });
  const { t: tState } = useTranslation(LocalizationNameSpace.MY_PLACE, {
    keyPrefix: 'screens.myPlace.state'
  });

  const { data, refetch, isPending, isRefetching, isError } = useGetOwnChats();

  const chats = useMemo(() => data?.filter((c) => !c.archived) ?? [], [data]);

  const listRef = useRef(null);
  useScrollToTop(listRef);

  const sharedValues = useMemo(() => ({ scrollOffset: scrollY }), [scrollY]);

  if (isPending) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <ActivityIndicator size={48} />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={[styles.center, { paddingTop: feedInset }]}>
        <Text variant="titleMedium">{tState('error.title')}</Text>
        <Text variant="bodyMedium">{tState('error.subtitle')}</Text>
        <Button mode="contained-tonal" onPress={() => refetch()}>
          {tState('error.retry')}
        </Button>
      </View>
    );
  }

  return (
    <AnimatedLegendList<ClientChat>
      ref={listRef}
      style={styles.list}
      data={chats}
      refreshControl={
        <Refresher refreshing={isRefetching} onRefresh={refetch} />
      }
      renderItem={({ item }) => <ChatListItem chat={item} />}
      recycleItems
      sharedValues={sharedValues}
      keyExtractor={(item) => item.id}
      ItemSeparatorComponent={() => <Divider />}
      ListEmptyComponent={
        <EmptyCard style={styles.empty} description={t('empty')} />
      }
      contentContainerStyle={[styles.content, { paddingTop: feedInset }]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    />
  );
};
